<template>
    <ControlPanel
        @apply="fetchData()"
        v-model:selectedTimePeriod="selectedTimePeriod"
        v-model:dateFrom="dateFrom"
        v-model:dateTo="dateTo"
        v-model:selectedOrderStatusType="selectedOrderStatusType"
        v-model:selectedSources="selectedSources"
    />
    <GeneralInformation
        :generalBySourceUuid="analytics.generalBySourceUuid"
        :orderSourcesByUuid="orderSourcesByUuid"
    />
    <OrdersCount
        :timeLineRange="timeLineRange"
        :orderSourcesByUuid="orderSourcesByUuid"
        :chartData="formatChartDataBySourceUuid(analytics.ordersCountByCreatedDateAndSourceUuid, timeLineRange, orderSourcesByUuid)"
        :color="getSourcesColor(analytics.ordersCountByCreatedDateAndSourceUuid, timeLineRange, orderSourcesByUuid)"
    />
    <OrdersSum
        :timeLineRange="timeLineRange"
        :orderSourcesByUuid="orderSourcesByUuid"
        :chartData="formatChartDataBySourceUuid(analytics.ordersSumByCreatedDateAndSourceUuid, timeLineRange, orderSourcesByUuid)"
        :color="getSourcesColor(analytics.ordersSumByCreatedDateAndSourceUuid, timeLineRange, orderSourcesByUuid)"
    />
    <OrderProfitSumByCreatedDate
        :timeLineRange="timeLineRange"
        :chartData="formatChartDataByStatus(analytics.ordersProfitSumByCreatedDateAndStatusType, timeLineRange)"
    />
    <OrderProfitSumByClosedDate
        :timeLineRange="timeLineRange"
        :chartData="formatChartDataBySourceUuid(analytics.ordersProfitSumByClosedDateAndStatusType, timeLineRange, orderSourcesByUuid)"
        :color="getSourcesColor(analytics.ordersProfitSumByClosedDateAndStatusType, timeLineRange, orderSourcesByUuid)"
    />
</template>
<script>
import ControlPanel from './cards/ControlPanel.vue';
import OrdersCount from './cards/OrdersCount.vue';
import OrdersSum from './cards/OrdersSum.vue';
import OrderProfitSumByCreatedDate from './cards/OrderProfitSumByCreatedDate.vue';
import OrderProfitSumByClosedDate from './cards/OrderProfitSumByClosedDate.vue';
import GeneralInformation from './cards/GeneralInformation.vue';

import { httpClient } from '../../../../services/HttpClient';
import { TimePeriodEnum } from '../../../enums/TimePeriodEnum';
import OrderSourcesModel from './models/OrderSourcesModel';
import AnalyticsModel from './models/AnalyticsModel';
import { OrderStatusTypeEnum } from '../../../enums/OrderStatusTypeEnum';

export default {
    name: 'AnalyticsIndexPage',
    components: {
        ControlPanel, OrdersCount, OrdersSum, OrderProfitSumByCreatedDate, OrderProfitSumByClosedDate, GeneralInformation
    },
    data() {
        const today = new Date();
        const dateTo = new Date();
        const dateFrom = new Date(today);
        dateFrom.setDate(dateFrom.getDate() - 30);

        return {
            timeLineRange: [],
            selectedTimePeriod: { value: TimePeriodEnum.DAY, label: this.$t('timePeriods.day') },
            dateFrom,
            dateTo,
            selectedOrderStatusType: [
                { value: OrderStatusTypeEnum.ACTIVE, label: this.$t('orderStatus.types.active') },
                { value: OrderStatusTypeEnum.DONE, label: this.$t('orderStatus.types.done') },
            ],
            selectedSources: [],
            analytics: new AnalyticsModel(),
            orderSourcesByUuid: {}
        }
    },
    methods: {
        async fetchData() {
            this.timeLineRange = this.getTimeLineRange();

            let requestBody = {
                date_from: this.formatLocalDate(this.dateFrom),
                date_to: this.formatLocalDate(this.dateTo),
                period: this.selectedTimePeriod.value,
            };

            if (this.selectedOrderStatusType.length > 0) {
                requestBody.status_types = this.selectedOrderStatusType.map(type => type.value);
            }

            if (this.selectedSources.length > 0) {
                requestBody.source_uuids = this.selectedSources.map(source => source.uuid);
            }

            await httpClient.get(`/projects/${this.$route.params.projectUuid}/crm/sources/analytics`, requestBody).then(response => {
                let responseData = response.data.data;

                this.analytics = new AnalyticsModel(
                    responseData.general_by_source_uuid,
                    responseData.orders_count_by_created_date_and_source_uuid,
                    responseData.orders_sum_by_created_date_and_source_uuid,
                    responseData.orders_profit_sum_by_created_date_and_status_type,
                    responseData.orders_profit_sum_by_closed_date_and_status_type,
                );
            });
        },
        async fetchOrderSources() {
            await httpClient.get(`/projects/${this.$route.params.projectUuid}/crm/sources`, {
                limit: 100
            }).then(response => {
                response.data.data.forEach(source => {
                    this.orderSourcesByUuid[source.uuid] = new OrderSourcesModel(
                        source.id,
                        source.name, 
                        source.uuid, 
                        source.project_uuid, 
                        source.integration_uuid, 
                        source.color, 
                        source.created_at, 
                        source.updated_at
                    );
                });
                this.orderSourcesByUuid["not_exists"] = new OrderSourcesModel(null, this.$t('crmAnalyticsPage.unknownSource'), "not_exists")
            });
        },
        formatLocalDate(date) {
            const y = date.getFullYear();
            const m = String(date.getMonth() + 1).padStart(2, '0');
            const d = String(date.getDate()).padStart(2, '0');
            return `${y}-${m}-${d}`;
        },
        getTimeLineRange() {
            if (!this.selectedTimePeriod?.value) {
                return [];
            }

            const dates = [];
            const current = new Date(this.dateFrom);
            const end = new Date(this.dateTo);
            const period = this.selectedTimePeriod.value;

            if (period === TimePeriodEnum.YEAR) {
                for (let y = current.getFullYear(); y <= end.getFullYear(); y++) {
                    dates.push(String(y));
                }
                return dates;
            }

            if (period === TimePeriodEnum.WEEK) {
                while (current <= end) {
                    const tempDate = new Date(Date.UTC(current.getFullYear(), current.getMonth(), current.getDate()));
                    const dayNum = tempDate.getUTCDay() || 7;
                    tempDate.setUTCDate(tempDate.getUTCDate() + 4 - dayNum);
                    const yearStart = new Date(Date.UTC(tempDate.getUTCFullYear(), 0, 1));
                    const weekNo = Math.ceil((((tempDate - yearStart) / 86400000) + 1) / 7);

                    const weekPart = `${tempDate.getUTCFullYear()}-${String(weekNo).padStart(2, '0')}`;

                    if (dates[dates.length - 1] !== weekPart) {
                        dates.push(weekPart);
                    }
                    current.setDate(current.getDate() + 1);
                }

                return dates;
            }

            if (period === TimePeriodEnum.MONTH) {
                current.setDate(1);
                const endMonthIndex = end.getFullYear() * 12 + end.getMonth();

                while (current.getFullYear() * 12 + current.getMonth() <= endMonthIndex) {
                    const m = String(current.getMonth() + 1).padStart(2, '0');
                    dates.push(`${current.getFullYear()}-${m}`);
                    current.setMonth(current.getMonth() + 1);
                }
                return dates;
            }

            while (current <= end) {
                dates.push(this.formatLocalDate(current));
                current.setDate(current.getDate() + 1);
            }

            return dates;
        },
        formatChartDataBySourceUuid(chartData, timeLineRange, orderSourcesByUuid) {
            const sources = Object.values(orderSourcesByUuid);
            const lineDataBySource = {};

            sources.forEach(source => {
                lineDataBySource[source.uuid] = [];
            });

            timeLineRange.forEach(date => {
                const dateData = chartData?.[date] || {};

                sources.forEach(source => {
                    lineDataBySource[source.uuid].push(dateData[source.uuid] || 0);
                });
            });

            const allSources = [...sources];

            return allSources
                .filter(source => lineDataBySource[source.uuid].some(value => value !== 0))
                .map(source => ({
                    name: source.name,
                    data: lineDataBySource[source.uuid]
                }));
        },
        getSourcesColor(chartData, timeLineRange, orderSourcesByUuid) {
            const sources = Object.values(orderSourcesByUuid);
            const lineDataBySource = {};

            sources.forEach(source => {
                lineDataBySource[source.uuid] = [];
            });

            timeLineRange.forEach(date => {
                const dateData = chartData?.[date] || {};

                sources.forEach(source => {
                    lineDataBySource[source.uuid].push(dateData[source.uuid] || 0);
                });
            });

            return sources
                .filter(source => lineDataBySource[source.uuid].some(value => value !== 0))
                .map(source => source.color || '#969696');
        },
        formatChartDataByStatus(chartData, timeLineRange) {
            const statusTypes = [
                { key: 'done', name: this.$t('crmAnalyticsPage.statusTypes.done') },
                { key: 'active', name: this.$t('crmAnalyticsPage.statusTypes.active') }
            ];
            
            const lineDataByStatus = {};

            statusTypes.forEach(type => {
                lineDataByStatus[type.key] = [];
            });

            timeLineRange.forEach(date => {
                const dateData = chartData[date] || {};

                statusTypes.forEach(type => {
                    lineDataByStatus[type.key].push(dateData[type.key] || 0);
                });
            });

            return statusTypes
                .filter(type => lineDataByStatus[type.key].some(value => value !== 0))
                .map(type => ({
                    name: type.name,
                    data: lineDataByStatus[type.key]
                }));
        }
    },
    computed: {
        sourcesColor() {
            return Object.values(this.orderSourcesByUuid).map(source => source.color || '#969696');
        }
    },
    async mounted() {
        await this.fetchOrderSources();
        await this.fetchData();
    }
}
</script>